// frontend/src/components/builder/PageLibrary.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPageConfigs, deletePageConfig, PageConfig } from '@/services/builderService';
import { FileText, Loader2, Trash2 } from 'lucide-react';

interface PageLibraryProps {
  onSelectPage: (page: PageConfig) => void;
}

const PageLibraryItem = ({ page, onSelect }: { page: PageConfig, onSelect: () => void }) => {
  return (
    <div
      onClick={() => onSelect()}
      className="flex items-center p-3 border rounded-lg hover:bg-gray-100 cursor-pointer transition-colors select-none"
    >
      <FileText className="h-6 w-6 mr-4 text-gray-600" />
      <div>
        <div className="font-semibold">{page.name}</div>
        <div className="text-sm text-gray-500">
          {page.app_type} - Last updated: {new Date(page.updated_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default function PageLibrary({ onSelectPage }: PageLibraryProps) {
  const [pages, setPages] = useState<PageConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchPages() {
      try {
        const pageConfigs = await getPageConfigs();
        setPages(pageConfigs);
      } catch (err) {
        setError('Failed to load page templates.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchPages();
  }, []);

  const handleSelect = (page: PageConfig) => {
    navigate(`/stitch/builder/${page.id}`);
    onSelectPage(page);
  };

  const handleDelete = async (pageId: number) => {
    try {
      await deletePageConfig(pageId);
      setPages(prev => prev.filter(p => p.id !== pageId));
    } catch (err) {
      alert("Failed to delete the page. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
        {!Array.isArray(pages) || pages.length === 0 ? (
            <p className="text-center text-gray-500">No page templates found.</p>
        ) : (
            pages.map((page) => (
                <PageLibraryItem 
                  key={page.id} 
                  page={page} 
                  onSelect={() => handleSelect(page)} 
                />
            ))
        )}
    </div>
  );
}
